import React, { useMemo, useState } from 'react';
import { Button, Card, Checkbox, Col, Input, Row, Select, Space, Statistic, Typography } from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import inventoryService, { type HeatStockFilters, type HeatStockRow } from '@/services/inventory.service';
import { useWarehouses } from '@/hooks/useInventory';
import { ItemSelect } from '@/components/inventory/ItemSelect';
import { HeatStockTable } from '@/components/inventory/HeatStockTable';
import { downloadCsv, downloadXlsx } from '@/reports/utils/exportTable';
import type { ReportColumnDef } from '@/reports/types';

const { Title, Text } = Typography;

const EXPORT_COLUMNS: ReportColumnDef[] = [
  { key: 'item_code', title: 'Item code', dataIndex: 'item_code' },
  { key: 'item_name', title: 'Item name', dataIndex: 'item_name' },
  { key: 'category_name', title: 'Category', dataIndex: 'category_name' },
  { key: 'critical', title: 'Critical', dataIndex: 'critical' },
  { key: 'warehouse', title: 'Warehouse', dataIndex: 'warehouse' },
  { key: 'location', title: 'Location', dataIndex: 'location' },
  { key: 'heat_number', title: 'Heat no', dataIndex: 'heat_number' },
  { key: 'dimension', title: 'Dimension', dataIndex: 'dimension' },
  { key: 'on_hand_quantity', title: 'On hand', dataIndex: 'on_hand_quantity' },
  { key: 'reserved_quantity', title: 'Reserved', dataIndex: 'reserved_quantity' },
  { key: 'free_quantity', title: 'Free', dataIndex: 'free_quantity' },
  { key: 'uom', title: 'UOM', dataIndex: 'uom' },
  { key: 'total_value', title: 'Value', dataIndex: 'total_value' },
  { key: 'mtc_document_number', title: 'MTC document', dataIndex: 'mtc_document_number' },
  { key: 'mtc_reference_number', title: 'MTC cert no', dataIndex: 'mtc_reference_number' },
  { key: 'source_number', title: 'Received on', dataIndex: 'source_number' },
  { key: 'source_date', title: 'Receipt date', dataIndex: 'source_date' },
  { key: 'supplier_name', title: 'Supplier', dataIndex: 'supplier_name' },
  { key: 'po_number', title: 'PO', dataIndex: 'po_number' },
  { key: 'ownership', title: 'Ownership', dataIndex: 'ownership' },
  { key: 'flags', title: 'Flags', dataIndex: 'flags' },
];

function toExportRow(r: HeatStockRow): Record<string, unknown> {
  return {
    ...r,
    critical: r.is_critical ? 'Yes' : 'No',
    warehouse: r.warehouse_code || r.warehouse_name || '',
    location: r.location_code || r.location_name || '',
    source_number: r.source?.source_number ?? '',
    source_date: r.source?.source_date ?? '',
    supplier_name: r.source?.supplier_name ?? '',
    po_number: r.source?.po_number ?? '',
    ownership: r.ownership_type === 'COMPANY' ? 'Company' : `${r.ownership_type}${r.owner_name ? ` (${r.owner_name})` : ''}`,
    flags: [r.heat_missing ? 'No heat' : '', r.mtc_missing ? 'No MTC' : ''].filter(Boolean).join(', '),
  };
}

const HeatStockPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [heatInput, setHeatInput] = useState(searchParams.get('heat_number') ?? '');
  const { data: warehouses = [] } = useWarehouses();

  const filters: HeatStockFilters = useMemo(
    () => ({
      item_id: searchParams.get('item_id') || undefined,
      warehouse_id: searchParams.get('warehouse_id') || undefined,
      heat_number: searchParams.get('heat_number') || undefined,
      critical_only: searchParams.get('critical_only') === '1',
      include_zero: searchParams.get('include_zero') === '1',
      mtc_status: (searchParams.get('mtc_status') as HeatStockFilters['mtc_status']) || 'all',
    }),
    [searchParams]
  );

  const setParam = (key: string, value: string | undefined) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const { data: rows = [], isFetching, refetch } = useQuery({
    queryKey: ['heat-wise-stock', filters],
    queryFn: () => inventoryService.getHeatWiseStock(filters),
  });

  const summary = useMemo(() => {
    const heats = new Set(rows.filter((r) => r.heat_number).map((r) => `${r.item_id}|${r.heat_number}`));
    return {
      heats: heats.size,
      noHeat: rows.filter((r) => r.heat_missing).length,
      noMtc: rows.filter((r) => r.mtc_missing).length,
      value: rows.reduce((s, r) => s + Number(r.total_value || 0), 0),
    };
  }, [rows]);

  const exportName = `heat-wise-stock-${dayjs().format('YYYYMMDD')}`;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>Heat-wise stock</Title>
        <Text type="secondary">
          Stock by heat number and dimension with MTC reference, receipt source and traceability gaps
        </Text>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Heats in stock" value={summary.heats} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable onClick={() => setParam('mtc_status', 'missing')}>
            <Statistic
              title="Critical stock without heat"
              value={summary.noHeat}
              valueStyle={{ color: summary.noHeat > 0 ? '#cf1322' : '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable onClick={() => setParam('mtc_status', 'missing')}>
            <Statistic
              title="Heats without MTC"
              value={summary.noMtc}
              valueStyle={{ color: summary.noMtc > 0 ? '#d46b08' : '#52c41a' }}
            />
            {summary.noMtc > 0 && (
              <Link to="/scm/quality/critical-mtc" onClick={(e) => e.stopPropagation()}>
                Fix in Critical MTC gaps
              </Link>
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Stock value"
              value={summary.value}
              precision={2}
              valueStyle={{ color: '#145A54' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Space wrap style={{ marginBottom: 16 }}>
          <ItemSelect
            style={{ width: 280 }}
            placeholder="All items"
            value={filters.item_id}
            onChange={(v) => setParam('item_id', (v as string) || undefined)}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 200 }}
            placeholder="All warehouses"
            value={filters.warehouse_id}
            onChange={(v) => setParam('warehouse_id', v || undefined)}
            options={warehouses.map((w) => ({ value: w.id, label: w.warehouse_name }))}
          />
          <Input.Search
            allowClear
            style={{ width: 200 }}
            placeholder="Heat number"
            value={heatInput}
            onChange={(e) => setHeatInput(e.target.value)}
            onSearch={(v) => setParam('heat_number', v.trim() || undefined)}
          />
          <Select
            style={{ width: 170 }}
            value={filters.mtc_status}
            onChange={(v) => setParam('mtc_status', v === 'all' ? undefined : v)}
            options={[
              { value: 'all', label: 'All MTC status' },
              { value: 'with', label: 'With MTC' },
              { value: 'missing', label: 'Missing heat / MTC' },
            ]}
          />
          <Checkbox
            checked={filters.critical_only}
            onChange={(e) => setParam('critical_only', e.target.checked ? '1' : undefined)}
          >
            Critical / heat-mandatory only
          </Checkbox>
          <Checkbox
            checked={filters.include_zero}
            onChange={(e) => setParam('include_zero', e.target.checked ? '1' : undefined)}
          >
            Include zero stock
          </Checkbox>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            Refresh
          </Button>
          <Button
            icon={<DownloadOutlined />}
            disabled={!rows.length}
            onClick={() => void downloadXlsx(exportName, EXPORT_COLUMNS, rows.map(toExportRow))}
          >
            Excel
          </Button>
          <Button
            icon={<DownloadOutlined />}
            disabled={!rows.length}
            onClick={() => downloadCsv(exportName, EXPORT_COLUMNS, rows.map(toExportRow))}
          >
            CSV
          </Button>
        </Space>

        <HeatStockTable rows={rows} loading={isFetching} />
      </Card>
    </div>
  );
};

export default HeatStockPage;
